<template>
    <div class="table-controls-wrapper">
        <el-input
            v-model="searchEmployment"
            :placeholder="$trans('profile_dossier.search_placeholder')"
            clearable
            style="width: 40%"
        >
            <template #suffix>
                <img
                    class="search-icon"
                    src="/media/images/search_gray.png"
                    alt="Search"
                />
            </template>
        </el-input>

        <EditButton
            class="ml-2 edit-button-vue"
            :onClick="redirectToEmploymentEdit"
        />
    </div>

    <EasyDataTable
        :headers="employmentHeaders"
        :items="sortedItems"
        :search-value="searchEmployment"
        table-class-name="employment-table"
        :rows-per-page="sortedItems.length"
        :buttons-pagination="false"
    >
        <template #item-action="{ id, is_selected }">
            <el-checkbox
                :key="`checkbox-${id}`"
                :checked="is_selected"
                @change="$emit('toggle', id)"
                :disabled="readOnly"
            />
        </template>

        <template #header-period_info>
            <div class="custom-table-header">
                <span
                    @click.stop="changeSort('to')"
                    :class="{ active: sortBy === 'to' }"
                >
                    {{ $trans("profile_dossier.from_to") }}
                    <SortIcons
                        :columnKey="'to'"
                        :sortBy="sortBy"
                        :sortDesc="sortDesc"
                    />
                </span>
                <span
                    @click.stop="changeSort('duration_sort')"
                    :class="{ active: sortBy === 'duration_sort' }"
                >
                    {{ $trans("profile_dossier.duration") }}
                    <SortIcons
                        :columnKey="'duration_sort'"
                        :sortBy="sortBy"
                        :sortDesc="sortDesc"
                    />
                </span>
            </div>
        </template>

        <template #item-period_info="{ from, to, duration_dossier }">
            <div class="cell-multiline">
                <div>
                    {{ formatDate(from) }} –
                    {{
                        to && to !== "-"
                            ? formatDate(to)
                            : $trans("profile_dossier.ongoing")
                    }}
                </div>
                <div>{{ duration_dossier }}</div>
            </div>
        </template>

        <template #header-org_info>
            <div class="custom-table-header">
                <span
                    @click.stop="changeSort('organization_name')"
                    :class="{ active: sortBy === 'organization_name' }"
                >
                    {{ $trans("profile_dossier.organization") }}
                    <SortIcons
                        :columnKey="'organization_name'"
                        :sortBy="sortBy"
                        :sortDesc="sortDesc"
                    />
                </span>
                <span
                    @click.stop="changeSort('position_title')"
                    :class="{ active: sortBy === 'position_title' }"
                >
                    {{ $trans("profile_dossier.position_project") }}
                    <SortIcons
                        :columnKey="'position_title'"
                        :sortBy="sortBy"
                        :sortDesc="sortDesc"
                    />
                </span>
            </div>
        </template>

        <template #item-org_info="{ organization_name, position_title }">
            <div class="cell-multiline">
                <div>{{ organization_name }}</div>
                <div>{{ position_title }}</div>
            </div>
        </template>

        <template #header-location_info>
            <div class="custom-table-header">
                <span
                    @click.stop="changeSort('city')"
                    :class="{ active: sortBy === 'city' }"
                >
                    {{ $trans("profile_dossier.city") }}
                    <SortIcons
                        :columnKey="'city'"
                        :sortBy="sortBy"
                        :sortDesc="sortDesc"
                    />
                </span>
                <span
                    @click.stop="changeSort('country')"
                    :class="{ active: sortBy === 'country' }"
                >
                    {{ $trans("profile_dossier.country") }}
                    <SortIcons
                        :columnKey="'country'"
                        :sortBy="sortBy"
                        :sortDesc="sortDesc"
                    />
                </span>
            </div>
        </template>

        <template #item-location_info="{ city, country }">
            <div class="cell-multiline">
                <div>{{ city }}</div>
                <div>{{ country }}</div>
            </div>
        </template>

        <template #item-job_description="{ job_description }">
            <template v-if="stripHtml(job_description).trim()">
                <div class="cell-content-wrapper">
                    <span class="text-content">
                        {{ stripHtml(job_description.slice(0, 50) + "...") }}
                    </span>

                    <img
                        src="/media/images/drupal_reference_icon.png"
                        alt="Reference Icon"
                        class="reference-icon"
                        @click="openModal(job_description)"
                    />
                </div>
            </template>
        </template>
    </EasyDataTable>

    <el-dialog :title='$trans("messages.description")' v-model="dialogVisible" width="50%">
        <p style="white-space: pre-wrap">{{ stripHtml(currentDescription) }}</p>
    </el-dialog>
</template>

<script>
import EasyDataTable from "vue3-easy-data-table";
import "vue3-easy-data-table/dist/style.css";
import { ElDialog } from "element-plus";
import SortIcons from "./SortIcons.vue";
import EditButton from "./../../../WebProfile/Components/EditButton.vue";

export default {
    name: "EmploymentTable",
    components: {
        EasyDataTable,
        ElDialog,
        SortIcons,
        EditButton,
    },
    props: {
        employmentItems: { type: Array, required: true },
        readOnly: { type: Boolean, default: false },
        profileId: { type: [Number, String], required: true },
    },
    emits: ["toggle"],
    data() {
        return {
            searchEmployment: "",
            dialogVisible: false,
            currentDescription: "",
            sortBy: "to",
            sortDesc: true,
            employmentHeaders: [
                {
                    text: this.$trans("profile_dossier.action"),
                    value: "action",
                    width: 10,
                },
                {
                    text: this.$trans("profile_dossier.period_info"),
                    value: "period_info",
                    sortable: true,
                },
                {
                    text: this.$trans("profile_dossier.org_info"),
                    value: "org_info",
                    sortable: true,
                },
                {
                    text: this.$trans("profile_dossier.location_info"),
                    value: "location_info",
                    sortable: true,
                },
                {
                    text: this.$trans("profile_dossier.project_summary"),
                    value: "job_description",
                },
            ],
        };
    },
    computed: {
        sortedItems() {
            if (!this.sortBy) return this.employmentItems;
            const items = [...this.employmentItems];

            items.sort((a, b) => {
                let valA = a[this.sortBy];
                let valB = b[this.sortBy];

                if (this.sortBy === "from" || this.sortBy === "to") {
                    const today = new Date();

                    valA = !valA || valA === "-" ? today : new Date(valA);
                    valB = !valB || valB === "-" ? today : new Date(valB);
                }

                if (this.sortBy === "duration_sort") {
                    valA = Number(a.duration_in_month ?? 0);
                    valB = Number(b.duration_in_month ?? 0);
                }

                if (valA == null) return 1;
                if (valB == null) return -1;

                if (valA < valB) return this.sortDesc ? 1 : -1;
                if (valA > valB) return this.sortDesc ? -1 : 1;
                return 0;
            });
            return items;
        },
    },
    methods: {
        stripHtml(html) {
            const div = document.createElement("div");
            div.innerHTML = html || "";
            return div.textContent || div.innerText || "";
        },
        formatDate(dateString) {
            if (!dateString) return "";
            const date = new Date(dateString);
            if (isNaN(date)) return dateString;
            return `${String(date.getMonth() + 1).padStart(
                2,
                "0"
            )}/${date.getFullYear()}`;
        },
        openModal(content) {
            this.currentDescription = content;
            this.dialogVisible = true;
        },
        changeSort(column) {
            if (this.sortBy === column) {
                this.sortDesc = !this.sortDesc;
            } else {
                this.sortBy = column;
                this.sortDesc = false;
            }
        },
        redirectToEmploymentEdit() {
            window.open(`/candidates/${this.profileId}/employment`, '_blank');
        },
    },
};
</script>

<style scoped>
:deep(.vue3-easy-data-table__main) {
    margin-bottom: unset !important;
}

:deep(.vue3-easy-data-table__main tr td) {
    height: 1px !important;
}

:deep(.vue3-easy-data-table__main tr th),
:deep(.vue3-easy-data-table__main tr td) {
    border: 2px solid #ededed !important;
}

:deep(.vue3-easy-data-table__main tr td) {
    background: #ffffff !important;
    padding: 0.5rem !important;
    line-height: 20px !important;
}

:deep(.vue3-easy-data-table__main tr th) {
    border-top: 0;
    border-bottom: 0;
    background: rgb(237, 237, 237);
    padding: 0 0.5rem;
    color: #3c3c3c;
    font-style: normal;
    font-weight: 400;
    font-size: 14px;
    height: 24px;
    line-height: 24px;
    font-family: sans-serif;
    border-top: unset;
}

:deep(.vue3-easy-data-table) {
    border: none !important;
}

:deep(.vue3-easy-data-table__footer) {
    background-color: transparent !important;
}

:deep(.vue3-easy-data-table__body tr td:first-child) {
    background-color: #d2e1e5;
}

:deep(.pagination__items-index) {
    display: none !important;
}

:deep(.el-input__prefix) {
    width: 0 !important;
}

:deep(.el-input__wrapper) {
    border-radius: 0 !important;
    height: 37px;
}

:deep(.vue3-easy-data-table__main) {
    background-color: #ededed;
}

:deep(.next-page__click-button .arrow) {
    margin-top: 21px;
    margin-left: 40px;
}

:deep(.vue3-easy-data-table__footer) {
    display: none !important;
}

.searcher {
    height: 40px;
    width: 295px;
    font-weight: 600;
}

:deep(.el-input__wrapper.is-focus) {
    border: 2px solid #41abc4 !important;
}

:deep(.pagination__rows-per-page) {
    display: none !important;
}

:deep(.vue3-easy-data-table__footer) {
    justify-content: center !important;
}

:deep(.vue3-easy-data-table__body tr:hover td:first-child) {
    background-color: #d2e1e5 !important;
}

:deep(.vue3-easy-data-table__body tr td:first-child) {
    text-align: center !important;
    background-color: #d2e1e5 !important;
}

.employment-table {
    --easy-table-header-font-size: 14px;
    --easy-table-body-row-font-size: 14px;
    --easy-table-border: none;

    --easy-table-body-row-hover-background-color: #fff;
}

.custom-table-header {
    display: flex;
    flex-direction: column;
    cursor: pointer;
    user-select: none;
    text-align: left;
}

.header-text {
    text-align: left;
}

:deep(.sortType-icon) {
    display: none !important;
}

.custom-table-header {
    display: flex !important;
    justify-content: space-between !important;
    width: 100% !important;
}

.employment-table th {
    position: relative !important;
}

.search-icon {
    width: 16px;
    height: 16px;
    opacity: 0.6;
}

.cell-content-wrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    width: 100%;
}

.text-content {
    flex-grow: 1;
    min-width: 0; 
}

.reference-icon {
    flex-shrink: 0; 
    width: 14px;
    height: 14px;
    cursor: pointer;
}

.table-controls-wrapper {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    margin-bottom: 20px;
}

.edit-button-vue {
    width: 24px !important;
    height: 24px !important;
}
</style>